import { useEffect, useState } from "react"
import { useTypedDispatch, useTypedSelector, RootState } from "../app/store"
import { useGetUserProfileQuery } from "../app/services/meseeknaApiV2"
import {
  convertAndUpdateOrganization,
  setRole,
  setLoggedInUser,
} from "../features/auth/authSlice"
import { User } from "../features/auth/types"

/**
 * Hook that attempts to recover organization ID when it's missing
 * @returns Object with recovery status
 */
export function useOrganizationRecovery() {
  const dispatch = useTypedDispatch()
  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const [isRecovering, setIsRecovering] = useState(false)
  const [recoveryAttempted, setRecoveryAttempted] = useState(false)

  // Skip if we already have a valid organization ID
  const shouldAttemptRecovery =
    !recoveryAttempted &&
    authStore.isAuthenticated &&
    (!authStore.organization?.id || authStore.organization.id === "0")

  // Query the user profile API to get the user's organization info
  const {
    data: profileData,
    isSuccess,
    isError,
  } = useGetUserProfileQuery(undefined, {
    skip: !shouldAttemptRecovery,
  })

  useEffect(() => {
    // Don't proceed if we're not attempting recovery or already in the process
    if (!shouldAttemptRecovery || isRecovering) return

    // Set recovering state
    setIsRecovering(true)

    // If profile data loaded successfully
    if (
      isSuccess &&
      profileData &&
      profileData.status === "success" &&
      profileData.data
    ) {
      const userData = profileData.data
      console.log("Organization recovery - User profile data:", userData)

      // Update organization in store with data from profile
      if (userData.organization) {
        dispatch(
          convertAndUpdateOrganization({
            status: "success",
            code: 200,
            data: {
              id: userData.organization.id,
              organizationName: userData.organization.name,
            },
          }),
        )

        // Update user in store if we have roles info
        if (userData.roles && userData.roles.length > 0) {
          const mainRole = userData.roles[0]
          const updatedUser: Partial<User> = {
            ...authStore.user,
            organizationId: userData.organizationId || undefined,
            role: mainRole.roleId,
            roleName: mainRole.name || undefined,
          }

          dispatch(setLoggedInUser(updatedUser as User))
          dispatch(
            setRole({
              roleId: mainRole.roleId,
              name: mainRole.name,
            }),
          )
        }
      }

      console.log("Organization recovery completed successfully")
    } else if (isError) {
      console.error("Failed to recover organization information")
    }

    // Mark recovery as attempted regardless of outcome
    setRecoveryAttempted(true)
    setIsRecovering(false)
  }, [
    shouldAttemptRecovery,
    isSuccess,
    isError,
    profileData,
    dispatch,
    authStore,
  ])

  return {
    isRecovering,
    recoverySuccessful: isSuccess && !!authStore.organization?.id,
    recoveryAttempted,
  }
}
